import { encounterField, inventoryField, itemsCol, layerOf, primaryPictureOf, posCoord } from '../shared/array-types';
import { isContainer, type MoveRuleReaders } from '../janus-engine/inventory/move-rules';

type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const rows = (v: unknown): Row[] => Array.isArray(v) ? v as Row[] : [];
const containerReaders: MoveRuleReaders = {
    value: layerOf, itemType: row => String(itemsCol(row, 'item_type') ?? ''),
    inventoryField, statusItemIds: () => [],
};
const opposite: Record<string, string> = {
    north: 'south', south: 'north', east: 'west', west: 'east',
    northeast: 'southwest', southwest: 'northeast', northwest: 'southeast', southeast: 'northwest',
};
function compass(name: string): string | undefined {
    const text = name.trim().toLowerCase();
    const match = /^(northeast|northwest|southeast|southwest|north|south|east|west|ne|nw|se|sw|n|s|e|w)\b/u.exec(text);
    if (match) return ({ ne: 'northeast', nw: 'northwest', se: 'southeast', sw: 'southwest', n: 'north', s: 'south', e: 'east', w: 'west' } as Record<string, string>)[match[1]!] ?? match[1];
    return ['northeast', 'northwest', 'southeast', 'southwest', 'north', 'south', 'east', 'west']
        .find(token => text.replace(/[-_]/g, ' ').includes(token));
}
function encounterLabel(host: Row): string {
    const prefix = encounterField(host, 'location_zone_prefix') ?? '';
    const num = encounterField(host, 'encounter_zone_number');
    const head = prefix !== '' || num !== undefined && num !== null && num !== ''
        ? [prefix, num].filter(v => v !== undefined && v !== null && v !== '').join(' ')
        : encounterField(host, 'encounter_zone') ?? '';
    return [head, encounterField(host, 'encounter_name')].filter(v => v !== undefined && v !== null && v !== '').join(' · ');
}
function option(row: Row, host: Row, selected: Set<string>): Row {
    const id = String(inventoryField(row, 'inventory_id') ?? '');
    const picture = primaryPictureOf(row);
    return { inventory_id: id, label: `${encounterLabel(host)} — ( ${posCoord(row, 'x_pos') ?? 0} | ${posCoord(row, 'z_pos') ?? 0} ) ${inventoryField(row, 'inventory_name') ?? ''}`,
        picture_url: picture ? pictureField0(picture, 'pic_url') ?? '' : '', selected: selected.has(upper(id)) };
}
function candidates(data: Row): { row: Row; host: Row }[] {
    return rows(data.inventory_hosts).flatMap(host => rows(host.encounter_inventories_arr).map(row => ({ row, host })));
}
function sortCandidates(values: { row: Row; host: Row }[]): void {
    values.sort((a, b) => {
        const compare = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
        const prefix = compare(String(encounterField(a.host, 'location_zone_prefix') ?? ''), String(encounterField(b.host, 'location_zone_prefix') ?? ''));
        if (prefix) return prefix;
        const number = Number(encounterField(a.host, 'encounter_zone_number') ?? 0) - Number(encounterField(b.host, 'encounter_zone_number') ?? 0);
        return number || compare(String(encounterField(a.host, 'encounter_name') ?? ''), String(encounterField(b.host, 'encounter_name') ?? ''));
    });
}

/** Accept already-rendered responses while bundles are being served before their PHP readers. */
export function renderEncounterLinkInputs(data: Row): Row {
    if (!Array.isArray(data.inventory_hosts)) return data;
    const selected = new Set((data.selected_ids as unknown[] ?? []).map(upper));
    const targetDirection = opposite[compass(String(data.inventory_name ?? '')) ?? ''];
    const values = candidates(data).filter(({ row }) => ['door', 'map_marker'].includes(String(itemsCol(row, 'item_type')))
        || /stair|ladder/i.test(String(inventoryField(row, 'inventory_name') ?? '')));
    sortCandidates(values);
    data.links = values.map(({ row, host }) => ({ ...option(row, host, selected),
        opposite_match: targetDirection !== undefined && compass(String(inventoryField(row, 'inventory_name') ?? '')) === targetDirection
            && itemsCol(row, 'item_type') === data.item_type && ['door', 'map_marker'].includes(String(data.item_type)) }));
    return data;
}

export function renderKeyLinkInputs(data: Row): Row {
    if (!Array.isArray(data.inventory_hosts)) return data;
    const selected = new Set((data.selected_ids as unknown[] ?? []).map(upper));
    const term = String(data.term ?? '').toLowerCase();
    const values = candidates(data).filter(({ row }) => {
        const name = String(inventoryField(row, 'inventory_name') ?? '');
        return upper(inventoryField(row, 'inventory_id')) !== upper(data.inventory_id)
            && (data.all_inventory || itemsCol(row, 'item_type') === 'door' || isContainer(row, containerReaders) || /stair|ladder/i.test(name))
            && (!term || name.toLowerCase().includes(term));
    });
    // Preserve the old cap's inclusive boundary and its pre-sort choice of rows.
    data.truncated = values.length >= 400;
    const limited = values.slice(0, 400);
    sortCandidates(limited);
    const links = limited.map(({ row, host }) => option(row, host, selected));
    const offered = new Set(links.map(link => upper(link.inventory_id)));
    for (const row of rows(data.selected_rows)) {
        const id = upper(inventoryField(row, 'inventory_id'));
        if (offered.has(id)) continue;
        const value = option(row, {}, selected);
        value.label = `[linked, outside scope] ${inventoryField(row, 'inventory_name') ?? id}`;
        links.push(value); offered.add(id);
    }
    data.links = links;
    data.encounters = rows(data.encounter_rows).map(host => ({ encounter_id: encounterField(host, 'encounter_id'),
        label: encounterLabel(host), selected: upper(encounterField(host, 'encounter_id')) === upper(data.encounter_scope_id) && data.encounter_scope_id !== '' }));
    return data;
}
